import { useState } from "react";
import { Plus } from "lucide-react";
import { Badge, Boton, Input } from "@/components/ui";
import { Chips, Dinero } from "@/components/ui/controles";
import { useDatos } from "../store/store";
import { gastoRenombrable, setFila } from "../store/acciones";
import { MODALIDADES, P, TIPO_FUENTE } from "../catalogo-ui";
import { anual, dinero } from "../util";
import { SeccionLayout, Bloque } from "../componentes/SeccionLayout";
import { Ejemplo } from "../componentes/Ejemplo";
import { PanelEnVivo } from "../componentes/PanelEnVivo";
import type { ExpenseInput, IncomeInput } from "@/engine/types";

function FilaIngreso({ r, cur, salarioMensual }: { r: IncomeInput; cur: string; salarioMensual: number }) {
  const apply = useDatos((x) => x.apply);
  const tipo = TIPO_FUENTE.get(r.source ?? "");
  const sospechaAnual = /bonos/i.test(r.source ?? "") && r.modality === "Mensual" && (r.amount ?? 0) > 3 * Math.max(1, salarioMensual) && salarioMensual > 0;
  const sospechaMiles = r.source === "Salario / sueldo neto" && r.amount !== null && r.amount > 0 && r.amount < 1000;
  return (
    <li className="flex flex-col gap-2 border-b border-borde py-3 last:border-0">
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium">{r.source}</span>
        {tipo && <Badge tono={tipo === "Pasivo" ? "info" : "neutro"}>{tipo === "Pasivo" ? "Pasivo · entra aunque no trabajes" : "Activo"}</Badge>}
      </div>
      <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
        <Dinero tamano="lg" valor={r.amount} moneda={cur} ariaLabel={`Monto de ${r.source}`} onChange={(v) => apply(setFila("incomes", r.row, { amount: v }))} />
        <Chips tamano="sm" className="self-center" etiqueta={`Cada cuándo: ${r.source}`} opciones={MODALIDADES} valor={r.modality} onChange={(v) => apply(setFila("incomes", r.row, { modality: v }))} />
      </div>
      {r.amount !== null && r.amount > 0 && r.modality !== "Anual" && <span className="text-xs text-tinta-suave">= {dinero(anual(r.amount, r.modality), cur)} al año</span>}
      {sospechaAnual && <span className="text-xs text-amber-800">¿Este monto es al año? Si sí, cámbialo a Anual.</span>}
      {sospechaMiles && <span className="text-xs text-amber-800">¿Está en miles? Escribe el monto completo, por ejemplo 110,000.</span>}
    </li>
  );
}

function FilaGasto({ r, cur }: { r: ExpenseInput; cur: string }) {
  const apply = useDatos((x) => x.apply);
  const renombrable = gastoRenombrable(r);
  return (
    <li className="flex flex-col gap-2 border-b border-borde py-3 last:border-0">
      {renombrable ? (
        <Input aria-label="Nombre del gasto" value={r.concept ?? ""} placeholder="Ponle nombre, p. ej. Ayuda a mi papá" onChange={(e) => apply(setFila("expenses", r.row, { concept: e.target.value }))} />
      ) : <span className="font-medium">{r.concept}</span>}
      <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
        <Dinero tamano="lg" valor={r.amount} moneda={cur} ariaLabel={`Monto de ${r.concept ?? "gasto"}`} onChange={(v) => apply(setFila("expenses", r.row, { amount: v }))} />
        <Chips tamano="sm" className="self-center" etiqueta={`Cada cuándo: ${r.concept}`} opciones={MODALIDADES} valor={r.modality} onChange={(v) => apply(setFila("expenses", r.row, { modality: v }))} />
      </div>
      {r.amount !== null && r.amount > 0 && r.modality !== "Mensual" && <span className="text-xs text-tinta-suave">= {dinero(anual(r.amount, r.modality) / 12, cur)} al mes · {dinero(anual(r.amount, r.modality), cur)} al año</span>}
      {renombrable && !r.concept && r.amount !== null && <span className="text-xs text-amber-800">Ponle nombre para que cuente.</span>}
    </li>
  );
}

export function Flujo() {
  const flow = useDatos((x) => x.inputs.flow);
  const cur = useDatos((x) => x.inputs.setup.currency) === "USD" ? "USD" : "MXN";
  const [extra, setExtra] = useState(0);
  const salario = flow.incomes.find((r) => r.source === "Salario / sueldo neto");
  const salarioMensual = salario ? anual(salario.amount, salario.modality) / 12 : 0;
  const otros = flow.expenses.filter(gastoRenombrable);
  const otrosVisibles = otros.filter((r, k) => r.amount !== null || (r.concept && !/^Otro \d$/.test(r.concept)) || k < extra);
  return (
    <div className="grid gap-6 md:grid-cols-[7fr_5fr]">
      <SeccionLayout id="flujo" intro="Tus ingresos y tus gastos, tal como te llegan. Con eso salen tu tasa de ahorro y tu % de libertad financiera.">
        <Bloque id="ingresos" titulo="Mis ingresos" ayuda="Solo llena las que tengas. El monto tal como te llega, después de impuestos.">
          <Ejemplo campo="ingresos" />
          <ul className="mt-2">{flow.incomes.map((r) => <FilaIngreso key={r.row} r={r} cur={cur} salarioMensual={salarioMensual} />)}</ul>
        </Bloque>
        <Bloque id="gastos" titulo="Mis gastos" ayuda="Todos. Los que duelen también. Aproxima: si dudas entre dos números, pon el de en medio y sigue.">
          <Ejemplo campo="gastos" />
          {P.expenseCategories.map((cat) => {
            const filas = flow.expenses.filter((r) => r.category === cat && (r.row < 51 || otrosVisibles.includes(r)));
            const mensual = flow.expenses.filter((r) => r.category === cat && r.concept).reduce((a, r) => a + anual(r.amount, r.modality), 0) / 12;
            return (
              <div key={cat} className="mt-4">
                <h3 className="flex items-baseline justify-between border-b border-borde pb-1 eyebrow text-tinta-suave"><span>{cat}</span><span className="tabular normal-case">{mensual > 0 ? `${dinero(mensual, cur)} al mes` : ""}</span></h3>
                <ul>{filas.map((r) => <FilaGasto key={r.row} r={r} cur={cur} />)}</ul>
                {cat === "Otros" && otrosVisibles.length < otros.length && <Boton variante="secundario" tamano="sm" className="mt-2" onClick={() => setExtra(otrosVisibles.length + 1)}><Plus className="h-4 w-4" aria-hidden /> Agregar otro gasto</Boton>}
                {cat === "Otros" && otrosVisibles.length >= otros.length && <p className="mt-2 text-xs text-tinta-suave">El modelo tiene 30 renglones de gasto; si te falta uno, súmalo en Imprevistos.</p>}
              </div>
            );
          })}
        </Bloque>
      </SeccionLayout>
      <PanelEnVivo />
    </div>
  );
}
